import { ChevronDown, ChevronRight, Copy, Info } from 'lucide-react';
import NextLink from 'next/link';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { formatToCompactCurrency, formatToCompactNumber, maskAddressValue } from 'src/common/utils/format';

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  Text,
  Tooltip,
} from '@/design-system/components/ui';

import { type ReferralTreeNode } from '../domain/types';

export interface ReferralTreeProps {
  tree: ReferralTreeNode | null;
  isLoading: boolean;
  slug: string;
}

interface TreeNodeProps {
  node: ReferralTreeNode;
  depth: number;
  defaultOpen?: boolean;
  slug: string;
}

function TreeNode({ node, depth, defaultOpen = false, slug }: TreeNodeProps) {
  const [open, setOpen] = useState(defaultOpen);
  const hasChildren = node.children.length > 0;
  const levelLabel = depth === 0 ? 'Affiliate' : `Level ${depth}`;

  return (
    <div className="tw:border-l-2 tw:border-fuul-blue/30 tw:rounded-sm">
      <div className="tw:flex tw:items-center tw:justify-between tw:py-3 tw:px-3 tw:gap-4">
        <div className="tw:flex tw:items-center tw:gap-3">
          {hasChildren ? (
            <button
              type="button"
              onClick={() => setOpen(!open)}
              className="tw:flex tw:items-center tw:justify-center tw:w-5 tw:h-5 tw:text-muted-foreground tw:hover:text-foreground tw:transition-colors"
              aria-label={open ? 'Collapse' : 'Expand'}
            >
              {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          ) : (
            <div className="tw:w-5" />
          )}

          <Badge variant="default" size="sm">
            {levelLabel}
          </Badge>

          <Text variant="body-sm" className="tw:font-mono">
            {maskAddressValue(node.user_identifier)}
          </Text>

          <Tooltip content="Copy address">
            <Button
              variant="ghost"
              size="icon"
              className="tw:h-6 tw:w-6"
              aria-label="Copy address"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(node.user_identifier);
                toast.success('Address copied');
              }}
            >
              <Copy size={12} />
            </Button>
          </Tooltip>
        </div>

        <div className="tw:flex tw:items-center tw:gap-4 tw:flex-wrap tw:justify-end">
          <div className="tw:text-right">
            <Text variant="body-sm" color="muted" className="tw:text-xs">
              Referred volume
            </Text>
            <Text variant="body-sm" weight="medium">
              {formatToCompactCurrency(node.referrer_volume)}
            </Text>
          </div>
          <div className="tw:text-right">
            <Text variant="body-sm" color="muted" className="tw:text-xs">
              Volume
            </Text>
            <Text variant="body-sm" weight="medium">
              {formatToCompactCurrency(node.total_volume)}
            </Text>
          </div>
          <div className="tw:text-right">
            <Text variant="body-sm" color="muted" className="tw:text-xs">
              Revenue
            </Text>
            <Text variant="body-sm" weight="medium">
              {formatToCompactCurrency(node.total_revenue)}
            </Text>
          </div>
          <div className="tw:text-right">
            <Text variant="body-sm" color="muted" className="tw:text-xs">
              Users
            </Text>
            <Text variant="body-sm" weight="medium">
              {formatToCompactNumber(node.users ?? node.traders ?? 0)}
            </Text>
          </div>
          <Tooltip
            side="right"
            arrow
            className="tw:bg-card tw:text-card-foreground tw:border tw:border-border tw:shadow-lg tw:max-w-[280px] [&_svg]:tw:fill-card"
            content={
              <div className="tw:flex tw:flex-col tw:gap-2 tw:p-1">
                <div>
                  <Text variant="body-sm" color="muted">
                    Wallet Address
                  </Text>
                  <Text variant="body-sm" weight="medium" className="tw:break-all">
                    {node.user_identifier}
                  </Text>
                </div>
                {node.project_affiliate_id && (
                  <NextLink
                    href={`/projects/${slug}/affiliates/${node.project_affiliate_id}`}
                    className="tw:text-sm tw:text-fuul-blue tw:hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    View affiliate detail →
                  </NextLink>
                )}
              </div>
            }
          >
            <Info size={14} className="tw:text-muted-foreground tw:cursor-pointer" />
          </Tooltip>
        </div>
      </div>

      {hasChildren && open && (
        <div className="tw:ml-6 tw:flex tw:flex-col tw:gap-1 tw:pb-2">
          {[...node.children]
            .sort((a, b) => b.referrer_volume - a.referrer_volume)
            .map((child) => (
              <TreeNode key={child.user_identifier} node={child} depth={depth + 1} slug={slug} />
            ))}
        </div>
      )}
    </div>
  );
}

export function ReferralTree({ tree, isLoading, slug }: ReferralTreeProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Referral Tree</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="tw:flex tw:flex-col tw:gap-3">
            <Skeleton variant="rectangular" height={48} />
            <Skeleton variant="rectangular" height={48} />
            <Skeleton variant="rectangular" height={48} />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!tree || tree.children.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Referral Tree</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="tw:flex tw:items-center tw:justify-center tw:py-16">
            <Text variant="body-md" color="muted">
              No referrals yet
            </Text>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Referral Tree</CardTitle>
        <Text variant="body-sm" color="muted">
          Click arrows to expand/collapse branches. Hover nodes for details.
        </Text>
      </CardHeader>
      <CardContent>
        <div className="tw:flex tw:flex-col tw:gap-2">
          <TreeNode node={tree} depth={0} defaultOpen slug={slug} />
        </div>
      </CardContent>
    </Card>
  );
}
